import Typography from "@bmi-digital/components/typography";
import ArrowForwardIcon from "@bmi-digital/components/icon/ArrowForward";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import React, { useCallback, useState } from "react";
import { useSiteContext } from "../Site";
import Icon from "../Icon";
import {
  StyledBrandLogo,
  StyledReadMoreButton,
  SubHeaderWrapper,
  SubHeaderContent,
  StyledPopover,
  StyledShareButton,
  TileColour,
  TileInformationBlock
} from "./styles/SubHeaderStyles";
import type { PIMTile } from "./Types";

export type VisualiserHeaderProps = {
  activeTile: PIMTile;
  onReadMoreBtnClick: ({
    type,
    label,
    data
  }: {
    type: string;
    label: string;
    data?: { variantCode: string; productPath: string };
  }) => void;
  shareWidget?: React.JSX.Element;
};

const SharePopover = ({ shareWidget }: { shareWidget: React.JSX.Element }) => {
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );
  const { getMicroCopy } = useSiteContext();

  const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorElement(null);
  };
  const visualiserSharePopoverLabel = getMicroCopy(
    microCopy.VISUALISER_SHARE_POPOVER_ACCESSIBILITY_LABEL
  );

  return (
    <div>
      <StyledShareButton
        variant="outlined"
        size="medium"
        accessibilityLabel={visualiserSharePopoverLabel}
        aria-describedby="share-popover"
        onClick={handlePopoverClick}
        data-testid="open-visualiser-share-popover-button"
        gtm={{
          id: "cta-click1",
          label: visualiserSharePopoverLabel
        }}
      >
        <Icon name="Share" />
      </StyledShareButton>
      <StyledPopover
        id="share-popover"
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={handlePopoverClose}
        data-testid="visualiser-share-popover"
        slotProps={{ paper: { square: true, elevation: 1 } }}
        className={"some-class-name"}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        {shareWidget}
      </StyledPopover>
    </div>
  );
};

const VisualiserSubHeader = (props: VisualiserHeaderProps) => {
  const { getMicroCopy } = useSiteContext();

  const handleReadMore = useCallback(
    () =>
      props.onReadMoreBtnClick({
        type: "product-link",
        label: getMicroCopy(microCopy.VISUALISER_READ_MORE),
        data: {
          variantCode: props.activeTile.code,
          productPath: props.activeTile.path
        }
      }),
    [getMicroCopy, props.activeTile, props.onReadMoreBtnClick]
  );

  const readMoreButtonlabel = getMicroCopy(microCopy.VISUALISER_READ_MORE);

  return (
    <SubHeaderWrapper data-testid="visualiser-sub-header">
      <SubHeaderContent>
        <StyledBrandLogo brandName={props.activeTile.brandCode} />
        <TileInformationBlock>
          <Typography variant="h4" data-testid="visualiser-tile-name">
            {props.activeTile.name}
          </Typography>
          <TileColour data-testid="visualiser-tile-colour">
            {props.activeTile.colour}
          </TileColour>
        </TileInformationBlock>
      </SubHeaderContent>
      <SubHeaderContent>
        <StyledReadMoreButton
          size="large"
          variant="outlined"
          data-testid="visualiser-read-more-button"
          endIcon={<ArrowForwardIcon />}
          onClick={handleReadMore}
          gtm={{
            id: "cta-click1",
            label: readMoreButtonlabel
          }}
        >
          {readMoreButtonlabel}
        </StyledReadMoreButton>
        {props.shareWidget && <SharePopover shareWidget={props.shareWidget} />}
      </SubHeaderContent>
    </SubHeaderWrapper>
  );
};

export default VisualiserSubHeader;
